import { useState, useCallback, useEffect } from 'react';
import { firstValueFrom } from 'rxjs';
import HttpClient from '../../../../utils/http-client';
import { getMunicipalities } from '../../../../usecases/thirds-usecase';
import { getAllSubContableAcounts } from '../../../../usecases/contable-acounts-usecase';
import { listener } from '../../../../utils/dispatch';

export default function usePipelineLookups({ companyId }) {
  const getClient = useCallback(() => {
    const uid = localStorage.getItem('user_uid') || '';
    const cid = companyId ? String(companyId) : (localStorage.getItem('cid') || null);
    return new HttpClient(uid, cid);
  }, [companyId]);

  const [products, setProducts] = useState([]);
  const [thirds, setThirds] = useState([]);
  const [spendTypes, setSpendTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const fetchLookups = useCallback(async () => {
    setLoadingLookups(true);
    try {
      const client = getClient();
      const uid = localStorage.getItem('user_uid') || '';
      const cid = companyId ? String(companyId) : (localStorage.getItem('cid') || null);
      const [prodRes, thirdRes, spendTypeRes, accRes, munisData, subaccountsData] = await Promise.all([
        firstValueFrom(client.get('product?page=1&limit=500')).catch(e => { console.warn('Products fetch failed', e); return null; }),
        firstValueFrom(client.get('thirds?page=1&limit=500&status=ACTIVE')).catch(e => { console.warn('Thirds fetch failed', e); return null; }),
        firstValueFrom(client.get('spends/spend_type?page=1&limit=500')).catch(e => { console.warn('SpendTypes fetch failed', e); return null; }),
        firstValueFrom(client.get('accounting/accounts?page=1&limit=200')).catch(e => { console.warn('Accounts fetch failed', e); return null; }),
        getMunicipalities("").catch(e => { console.warn('Municipalities fetch failed', e); return []; }),
        getAllSubContableAcounts(uid, cid, false).catch(e => { console.warn('Subaccounts fetch failed', e); return []; })
      ]);

      const parseList = (res) => {
        if (!res) return [];
        const d = (res.config || res.headers) ? res.data : res;
        if (!d) return [];
        if (d?.data?.data && Array.isArray(d.data.data)) return d.data.data;
        if (d?.data && Array.isArray(d.data)) return d.data;
        if (Array.isArray(d)) return d;
        return [];
      };

      setProducts(parseList(prodRes));
      setThirds(parseList(thirdRes));
      setSpendTypes(parseList(spendTypeRes));
      setAccounts(parseList(accRes));
      setMunicipalities(munisData || []);
      setSubaccounts(Array.isArray(subaccountsData) ? subaccountsData.filter(s => s && s.message !== 'empty_array') : []);
    } catch (e) {
      console.error("Error fetching lookup data", e);
    } finally {
      setLoadingLookups(false);
    }
  }, [companyId, getClient]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  // Refresh lookups when a third party is created
  useEffect(() => {
    const cleanup = listener('update-thirds', () => fetchLookups());
    return () => cleanup();
  }, [fetchLookups]);

  return {
    products, thirds, spendTypes, accounts, subaccounts, municipalities, loadingLookups,
    fetchLookups, setSpendTypes, getClient
  };
}
